import * as React from "react";
import { XIcon, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

/**
 * ResponsiveDialog — one API, two presentations:
 *  - desktop (>= lg): a centered `Dialog`
 *  - mobile  (<  lg): a swipe-to-dismiss bottom `Drawer`
 *
 * The breakpoint decision is made once at the Root and shared via context so the
 * Trigger/Content/Header/... all render the matching primitive. Pass desktop layout
 * classes via `className`; the mobile sheet keeps its own full-width sizing (override
 * with `drawerClassName` only if needed).
 */

const ResponsiveDialogContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
});

const useResponsiveDialogContext = () =>
  React.useContext(ResponsiveDialogContext);

interface ResponsiveDialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  children?: React.ReactNode;
}

const ResponsiveDialog = ({ children, ...props }: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();
  const Root = isMobile ? Drawer : Dialog;

  return (
    <ResponsiveDialogContext.Provider value={{ isMobile }}>
      <Root {...props}>{children}</Root>
    </ResponsiveDialogContext.Provider>
  );
};

const ResponsiveDialogTrigger = ({
  children,
  ...props
}: React.ComponentProps<typeof DialogTrigger>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Trigger = isMobile ? DrawerTrigger : DialogTrigger;
  return <Trigger {...props}>{children}</Trigger>;
};

const ResponsiveDialogClose = ({
  children,
  ...props
}: React.ComponentProps<typeof DialogClose>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Close = isMobile ? DrawerClose : DialogClose;
  return <Close {...props}>{children}</Close>;
};

// Near drop-in for DialogContent: on desktop it forwards every DialogContent prop
// (className, disableClose, dialogId, maximized, ...); on mobile it renders the bottom
// sheet, which keeps its own full-width sizing and ignores Dialog-only props (override
// the sheet with `drawerClassName` if needed).
type ResponsiveDialogContentProps = React.ComponentProps<
  typeof DialogContent
> & {
  drawerClassName?: string;
  /** Render the mobile drawer body full-bleed (no default p-4/gap-4 wrapper). */
  drawerBare?: boolean;
};

const ResponsiveDialogContent = React.forwardRef<
  HTMLDivElement,
  ResponsiveDialogContentProps
>(({ drawerClassName, drawerBare, children, ...props }, ref) => {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return (
      <DrawerContent ref={ref} className={drawerClassName} bare={drawerBare}>
        {children}
      </DrawerContent>
    );
  }

  return (
    <DialogContent ref={ref} {...props}>
      {children}
    </DialogContent>
  );
});
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

const ResponsiveDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Header = isMobile ? DrawerHeader : DialogHeader;
  return <Header className={className} {...props} />;
};

const ResponsiveDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Footer = isMobile ? DrawerFooter : DialogFooter;
  return <Footer className={className} {...props} />;
};

const ResponsiveDialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogTitle>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Title = isMobile ? DrawerTitle : DialogTitle;
  return <Title className={className} {...props} />;
};

const ResponsiveDialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogDescription>) => {
  const { isMobile } = useResponsiveDialogContext();
  const Description = isMobile ? DrawerDescription : DialogDescription;
  return <Description className={className} {...props} />;
};

/**
 * ModalHeader — the one shared header for every modal so their tops stay
 * consistent: an optional icon tile, title, and description on the left; an
 * optional actions slot (badges, maximize, ...) and a close button on the right.
 * Works in both the desktop Dialog and the mobile Drawer via the Responsive*
 * primitives. Pass `showClose={false}` to omit the close button.
 */
type ModalHeaderProps = {
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  showClose?: boolean;
  className?: string;
  titleClassName?: string;
};

const ModalHeader = ({
  icon: Icon,
  title,
  description,
  actions,
  showClose = true,
  className,
  titleClassName,
}: ModalHeaderProps) => {
  return (
    <ResponsiveDialogHeader
      className={cn(
        "border-border/60 flex flex-row items-start justify-between gap-3 space-y-0 border-b pb-4 text-left",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {Icon ? (
          <div className="border-primary/25 bg-primary/10 text-primary shrink-0 rounded-2xl border p-2 lg:p-2.5">
            <Icon className="size-5" aria-hidden="true" />
          </div>
        ) : null}
        <div className="min-w-0 space-y-1">
          <ResponsiveDialogTitle
            className={cn(
              "text-foreground text-lg font-semibold tracking-tight lg:text-xl",
              titleClassName,
            )}
          >
            {title}
          </ResponsiveDialogTitle>
          {description ? (
            <ResponsiveDialogDescription className="text-muted-foreground text-sm">
              {description}
            </ResponsiveDialogDescription>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {actions}
        {showClose ? (
          <ResponsiveDialogClose asChild>
            <Button variant="secondary" size="icon" className="rounded-xl">
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </Button>
          </ResponsiveDialogClose>
        ) : null}
      </div>
    </ResponsiveDialogHeader>
  );
};

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ModalHeader,
};
